import { useMemo, useState } from "react";
import { FileText, Plus, Search, Star } from "lucide-react";
import PageHeader from "../../components/ui/PageHeader";
import NoDataState from "../../components/NoDataState";
import { useAlert } from "../../../hooks/useAlert";

import TemplateCard from "./templates/template-card";
import TemplatePreviewDrawer from "./templates/template-preview-drawer";
import { TEMPLATES, TEMPLATE_CATEGORIES } from "./templates/template.data";

export default function Templates() {
  const { showAlert } = useAlert();
  const [items, setItems] = useState(TEMPLATES);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [favOnly, setFavOnly] = useState(false);
  const [preview, setPreview] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((t) => {
      const matchCat = category === "all" || t.category === category;
      const matchFav = !favOnly || t.favorite;
      const matchSearch = !q || t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
      return matchCat && matchFav && matchSearch;
    });
  }, [items, category, search, favOnly]);

  const toggleFav = (tpl) => {
    setItems((prev) => prev.map((t) => (t.id === tpl.id ? { ...t, favorite: !t.favorite } : t)));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Templates"
        subtitle="Reusable email designs for every kind of campaign."
        icon={FileText}
        actions={
          <button
            onClick={() => showAlert({ type: "info", message: "Template builder coming soon." })}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl text-white"
            style={{ background: "linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)", boxShadow: "0 4px 16px rgba(53,53,93,0.22)" }}
          >
            <Plus className="w-4 h-4" /> New Template
          </button>
        }
      />

      {/* Filter bar */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-1 overflow-x-auto rounded-xl bg-surface_alt p-1 border border-border">
          {TEMPLATE_CATEGORIES.map((c) => {
            const active = category === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors"
                style={active
                  ? { background: "var(--color-surface)", color: "var(--color-primary)", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }
                  : { color: "var(--color-text-muted)" }}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setFavOnly((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-colors"
            style={favOnly
              ? { background: "var(--color-warning-bg)", borderColor: "var(--color-warning-border)", color: "var(--color-warning)" }
              : { background: "var(--color-surface)", borderColor: "var(--color-border)", color: "var(--color-text-muted)" }}
          >
            <Star className="w-3.5 h-3.5" style={{ fill: favOnly ? "var(--color-warning)" : "transparent" }} />
            Favorites
          </button>
          <div className="relative w-full lg:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text_muted pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-9 pr-4 py-2 text-sm rounded-xl bg-surface border border-border text-text_primary placeholder:text-text_muted focus:outline-none focus:border-primary transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Gallery */}
      {filtered.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {filtered.map((t) => (
            <TemplateCard key={t.id} template={t} onPreview={setPreview} onToggleFav={toggleFav} />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-surface">
          <NoDataState title="No templates found" subtitle="Try a different category, clear the favorites filter, or change your search." icon="📭" />
        </div>
      )}

      <TemplatePreviewDrawer template={preview} open={!!preview} onClose={() => setPreview(null)} />
    </div>
  );
}
